// The media library and the image pipeline behind it. Format names, densities
// and srcset keep their English form — they name what the browser and the
// encoder call these things, and a German word for AVIF would name nothing.
import type { CodeImageUsageKey } from '../../constants'
import type { ImageCropMode } from '../../enums'

import { CATALOGUE } from './catalogue'
import { COMMON } from './common'
import { CONFIGURATOR_CMS } from './configurator-cms'
import { CONTROLS } from './controls'
import { FIELD_LABELS } from './fields'
import { TERMS } from './terms'

export const MEDIA = {
  title: 'Medien',

  search: CONFIGURATOR_CMS.media.searchImages,
  empty: 'Noch keine Bilder',
  noResults: 'Keine Bilder gefunden',
  /** The noun the toolbar builds its count out of, in both numbers. */

  // ─── Views ──────────────────────────────────────────────────────────
  viewGrid: 'Kacheln',
  viewList: 'Liste',
  /** The three steps the grid is drawn at. Letters, because the number of
   *  pixels an edge measures is not a thing anyone reasons about. */
  tileSizes: { S: 'Kleine Kacheln', M: 'Mittlere Kacheln', L: 'Große Kacheln' },

  // ─── Filters ────────────────────────────────────────────────────────
  filterTagModeAll: 'Alle Tags',
  filterTagModeAny: 'Irgendein Tag',
  filterFormat: 'Format',
  filterPeriod: 'Zeitraum',
  // The number field sits beside the label in the filter chip, so the pair
  // reads as one phrase: "Breite ab 800".
  filterMinWidth: 'Breite ab',
  filterMinHeight: 'Höhe ab',
  filterPeriodLabels: {
    ALL: CONTROLS.filter.all,
    DAYS_7: 'Letzte 7 Tage',
    DAYS_30: 'Letzte 30 Tage',
    DAYS_365: 'Letztes Jahr',
  },
  filterUnused: COMMON.onlyUnused,
  collections: 'Sammlungen',
  sortLabels: {
    NEWEST: 'Neueste zuerst',
    OLDEST: 'Älteste zuerst',
    TITLE: FIELD_LABELS.title.label,
    SIZE: 'Dateigröße',
    USAGE: TERMS.usagePlural,
  } as const,

  // ─── Columns ────────────────────────────────────────────────────────
  columns: {
    preview: COMMON.preview,
    title: FIELD_LABELS.title.label,
    format: 'Format',
    dimensions: 'Abmessungen',
    fileSize: COMMON.size,
    created: 'Hochgeladen',
    usages: TERMS.usagePlural,
    tags: CONFIGURATOR_CMS.media.tags,
  },

  // ─── Selection and bulk actions ─────────────────────────────────────
  selectedCount: (n: number) =>
    n === 1 ? '1 Bild ausgewählt' : `${n} Bilder ausgewählt`,
  bulkDownload: COMMON.download,
  // The archive the bulk download hands over, and the fallback name for an
  // asset whose title is empty or all separators.
  downloadFileName: 'bilder.zip',
  untitledFileName: 'bild',
  bulkTagAdd: COMMON.add,
  bulkDelete: COMMON.delete,
  // §9.1 organises the library by tags and rejects a folder tree, so moving
  // is re-filing: out of the tags being filtered on, into the one picked.
  bulkMove: 'Verschieben',
  bulkMoveTo: (tag: string) => `Nach „${tag}“ verschieben`,
  bulkMoveHint: (from: string[]) =>
    from.length === 1
      ? `Entfernt „${from[0]}“ und setzt den gewählten Tag.`
      : `Entfernt ${from.map(tag => `„${tag}“`).join(', ')} und setzt den gewählten Tag.`,
  bulkMoveNeedsFilter:
    'Filtere zuerst nach einem Tag — verschoben wird aus diesem heraus.',
  bulkDeleteConfirm:
    'Die ausgewählten Bilder werden gelöscht. Bilder, auf die noch etwas zeigt, bleiben stehen.',

  // ─── Detail ─────────────────────────────────────────────────────────
  detailTitle: FIELD_LABELS.title.label,
  detailTitlePlaceholder: CONFIGURATOR_CMS.media.imageTitlePlaceholder,
  detailTags: CONFIGURATOR_CMS.media.tags,
  lightbox: 'Groß anzeigen',
  lightboxPosition: (index: number, total: number) => `${index} von ${total}`,
  zoomIn: COMMON.zoomIn,
  zoomOut: COMMON.zoomOut,
  zoomFit: 'Einpassen',
  /** Under the zoom figure: what the picture answers to besides the buttons. */
  zoomHint: 'Mausrad zoomt, Doppelklick passt ein, Ziehen verschiebt',
  detailDerivatives: 'Abgeleitete Dateien',
  noDerivatives:
    'Noch keine abgeleiteten Dateien. Bis dahin wird die alte Variante ausgeliefert.',
  replace: 'Bild ersetzen',
  replaceHint:
    'Die Verknüpfungen bleiben bestehen — überall, wo das Bild verwendet wird, erscheint das neue.',

  /** Heads the block: the picture, the three tools over it, and what each
   *  format keeps. A steady name — what the reader is currently doing is said
   *  under the picture, not here. */
  corrections: 'Bildkorrektur',
  /** Over the live previews under the picture. */
  focalPreview: 'So wird zugeschnitten',
  focalHint:
    'Klick ins Bild setzt den Punkt. Wo ein Format enger ist als das Bild, bleibt er im Ausschnitt.',
  focalNoCrops: 'Kein Format schneidet dieses Bild zu.',
  /** What an asset's derived files are, where that is worth saying. */
  renderState: {
    rendering: 'Zuschnitte werden neu berechnet …',
    pending: 'Zuschnitte noch nicht berechnet.',
  },

  usedBy: 'Verwendet von',
  notUsed: CATALOGUE.usage.empty,
  duplicateWarning: (title: string) =>
    title
      ? `Dieses Bild liegt bereits als „${title}” in der Bibliothek.`
      : 'Dieses Bild liegt bereits in der Bibliothek.',
  usageKinds: {
    CONFIGURATOR_QUESTION: 'Section',
    MEASURE_IMAGE: `${TERMS.measure} — Bild`,
    PRODUCT_IMAGE: `${TERMS.product} — Bild`,
  } as const,

  deleteConfirm: CONFIGURATOR_CMS.media.deleteImageConfirm,
  deleteBlocked: (n: number) =>
    n === 1
      ? 'Auf dieses Bild zeigt noch eine Stelle. Entferne sie zuerst.'
      : `Auf dieses Bild zeigen noch ${n} Stellen. Entferne sie zuerst.`,

  // ─── Upload ─────────────────────────────────────────────────────────
  uploadDrop: 'Bilder hier ablegen',
  uploadPick: 'Bilder hochladen',
  uploadFormats: 'JPG, PNG, WebP oder AVIF',
  uploadRejected: (n: number) =>
    n === 1
      ? 'Eine Datei wurde übersprungen: falsches Format oder zu groß.'
      : `${n} Dateien wurden übersprungen: falsches Format oder zu groß.`,
  uploading: (done: number, total: number) =>
    `${done} von ${total} hochgeladen`,

  duplicateFound: 'Dieses Bild gibt es schon',
  duplicateHint:
    'Ein optisch identisches Bild liegt bereits in der Bibliothek. Du kannst das neue behalten oder es löschen und stattdessen das vorhandene verwenden.',
  duplicateUseExisting: 'Vorhandenes verwenden',
  duplicateKeepBoth: 'Beide behalten',

  // ─── Resolution warnings ────────────────────────────────────────────
  resolutionWarning: 'Auflösung zu gering',
  resolutionWarningFor: (
    usage: string,
    width: number,
    height: number | null,
  ) =>
    height
      ? `Zu klein für „${usage}” — mindestens ${width} × ${height} px`
      : `Zu klein für „${usage}” — mindestens ${width} px breit`,

  // ─── Renditions (system area) ───────────────────────────────────────
  usagesTitle: 'Renditionen',
  usagesSubtitle:
    'Jedes hochgeladene Bild wird in diese Formate gerechnet. Mehr Renditionen heißt mehr Dateien pro Bild und längere Berechnung.',
  /** What each rendition the code renders by name is actually drawn on. A
   *  rendition an operator adds has no entry and shows none. */
  renditionSurfaces: {
    PREVIEW: 'Konfigurator-Kachel, Medienbibliothek und Bildauswahl.',
    DETAIL: 'Maßnahmen-Vollbild und Produkt-Panel.',
    ICON: 'Produkt neben seinem Namen.',
  } as const satisfies Record<CodeImageUsageKey, string>,
  usageKey: 'Key',
  usageKeyLocked: 'Wird vom Code angefragt und kann nicht umbenannt werden.',
  usageTargetWidth: 'Breite',
  usageAspect: 'Seitenverhältnis',
  usageAspectFree: 'Originalformat',
  usageCrop: 'Zuschnitt',
  usageCropNone: 'Nichts wird geschnitten',
  usageDensities: 'Auflösungsstufen',
  densityHint:
    'Dieselbe Datei mehrfach, damit jeder Bildschirm die passende bekommt. Der Browser lädt genau eine davon — mehr Stufen heißen also nicht mehr Ladezeit, sondern mehr Speicher und längere Berechnung.',
  densitySteps:
    '1× normale Monitore · 2× Retina und fast alle Handys · 3× die schärfsten Handys',
  densityLabel: (density: number, width: number, height: number | null) =>
    height ? `${density}× · ${width} × ${height}` : `${density}× · ${width} px`,
  usageMinimum: (width: number, height: number | null) =>
    height
      ? `Originale ab ${width} × ${height} px`
      : `Originale ab ${width} px Breite`,
  usageMinimumHint: 'Kleinere werden hochgerechnet und wirken unscharf.',
  usageDerivatives: (n: number) =>
    n === 1 ? '1 Bild gerechnet' : `${n} Bilder gerechnet`,
  usageTooSmall: (n: number) =>
    n === 1 ? '1 Bild zu klein' : `${n} Bilder zu klein`,
  addUsage: 'Rendition hinzufügen',
  deleteUsage: 'Rendition löschen',
  deleteUsageConfirm:
    'Die Rendition und alle daraus erzeugten Dateien werden gelöscht.',

  cropLabels: {
    FOCAL_POINT: 'Um den Fokuspunkt',
    CENTER: 'Mittig',
    CONTAIN: 'Ganz einpassen',
  } as const satisfies Record<ImageCropMode, string>,

  // ─── Reprocessing ───────────────────────────────────────────────────
  reprocessTitle: 'Neu berechnen',
  reprocessSubtitle:
    'Bilder ohne aktuelle Dateien werden im Hintergrund neu berechnet. Bis ein Bild fertig ist, wird die alte Variante ausgeliefert.',
  reprocessPending: (pending: number, total: number) =>
    `${pending} von ${total} offen`,
  reprocessDone: 'Alle Bilder sind aktuell',
  reprocessStart: 'Neu berechnen starten',
  rotateLeft: 'Nach links drehen',
  rotateRight: 'Nach rechts drehen',
  crop: 'Zuschneiden',
  cropHint:
    'Ziehe ein Rechteck auf. Was darin liegt, bleibt; der Rest wird abgeschnitten.',
  cropReset: 'Zuschnitt zurücksetzen',
  cropApplied: 'Zugeschnitten',

  uploadTooSmall: (usages: string) => `Zu klein für ${usages}`,
  pickTooSmall: (usages: string) =>
    `Zu klein für ${usages} — wird beim Rendern hochskaliert.`,

  uploadTitlePrefix: 'Titel für alle',
  uploadTitlePrefixPlaceholder: 'z. B. Parkett Eiche',
  uploadTagsForAll: 'Tags für alle',
  uploadCount: (n: number) =>
    n === 1 ? 'Ein Bild hochladen' : `${n} Bilder hochladen`,

  uploadFromUrl: 'Bild per Link laden',
  uploadUrl: 'Bild-Link',
  uploadUrlPlaceholder: 'https://…',

  errors: {
    urlNotAnImage: 'Unter diesem Link liegt kein Bild.',
    uploadChanged:
      'Die hochgeladene Datei hat sich nach der Prüfung geändert. Bitte lade sie erneut hoch.',
    uploadNotStaged: (maxMb: number) =>
      `Der Upload konnte nicht übernommen werden. Erlaubt sind bis zu ${maxMb} MB.`,
    usageRenderedByCode: (key: string) =>
      `Die Verwendung „${key}” wird im Code namentlich angefordert und kann nicht umbenannt oder gelöscht werden. Ihre Geometrie lässt sich weiterhin ändern.`,
  },
} as const
